import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactTable, { Column } from 'react-table-6';
import { markCallFolderAsRead } from '../../api/history-control';
import { HasClassName } from '../../component-utils';
import { useSelector } from '../../hook-utils';
import { showConfirmWindow } from '../../legacy';
import { Call, CallFolder, DeviceType, ExtensionStatus } from '../../model';
import { actions } from '../../store';
import { selectCalls, selectMissedCallsCount } from '../../store/reducers/calls';
import { selectDateFormating, selectMyAccess, selectMyself } from '../../store/reducers/config';
import { getGridSortOrder } from '../../store/reducers/localSettings';
import { selectExtension } from '../../store/reducers/queueAgents';
import { TableRow } from '../../table-utils';
import { formatRelativeDateTime } from '../AgentsTable';
import { useCompactView } from '../compactView';
import { ReactComponent as CallHistoryDesk } from '../icons/dial-desk.svg';
import { ReactComponent as CallHistoryHome } from '../icons/dial-home.svg';
import { ReactComponent as CallHistoryMobile } from '../icons/dial-mobile.svg';
import { ReactComponent as CallHistoryOther } from '../icons/dial-other.svg';
import { ReactComponent as CallHistorySoftphone } from '../icons/dial-softphone.svg';
import { ReactComponent as CallHistoryEmail } from '../icons/email.svg';
import { ReactComponent as MediaCallBackIcon } from '../icons/media/callback.svg';
import { ReactComponent as FacebookIcon } from '../icons/media/facebook.svg';
import { ReactComponent as OutBoundIcon } from '../icons/media/outbound.svg';
import { ReactComponent as TweetIcon } from '../icons/media/tweet.svg';
import { ReactComponent as WebChatIcon } from '../icons/media/web-chat.svg';
import { ReactComponent as SearchIcon } from '../icons/toolbar-search.svg';
import Input from '../Input';
import { Menu, MenuDivider, MenuItem, MenuTarget, targetFromEvent } from '../Menu';
import MoreButton from '../MoreButton';
import { ItemSelected } from '../SearchPage';
import { TabButton, TabsHeader } from '../Tabs';
import './CallsPage.scss';


interface Props extends HasClassName {
  history: Call
}

const FolderNames: CallFolder[] = ['missed', 'received', 'placed']

function getColumns(folder: CallFolder, onMore: (state: MenuState) => void): Column<Call>[] {
  const cols: Column<Call>[] = [{
    id: 'icon',
    Header: '',
    width: 32,
    resizable: false,
    accessor: c => <CallHistoryIcon history={c} />,
  }, {
    id: 'name',
    Header: folder === 'placed' ? 'To' : 'From',
    accessor: c => callHistortyName(c),
  }]

  return [
    ...cols,
    {
      id: 'date',
      Header: 'Date',
      accessor: c => c.date,
      Cell: ({ original: r }) => <RelativeLongFormatDateTime date={r.date} />,
    }, {
      id: 'menuButton',
      Cell: ({ original: call }: TableRow<Call>) => <div className='actions'>
        <MoreButton className='menu-button' onClick={e => {
          e.stopPropagation()
          onMore({ target: targetFromEvent(e), item: call })
        }
        } />
      </div>,
      width: 40,
      sortable: false,
    },
  ]
}

interface MenuState {
  target: MenuTarget
  item: Call
}

export default function MobileCallsPage() {
  console.log('Render CallsPage')

  const dispatch = useDispatch()
  const [folder, setFolder] = useState(FolderNames[0])
  const data = useSelector(state => selectCalls(state, folder))
  const [menu, setMenu] = useState<MenuState>()
  const [selected, setSelected] = useState<string>()
  // eslint-disable-next-line
  const [selection, setSelection] = useState<ItemSelected>()
  const unread = useSelector(selectMissedCallsCount) || 0
  const compactView = useCompactView()
  const [filter, setFilter] = useState('')
  const selectGridItem = useSelectGridItem()
  const sorted = useSelector(s => getGridSortOrder(s, 'recent'))
  const reactTable = useRef<ReactTable<Call>>(null);


  useEffect(() => {
    return () => {
      setSelected(undefined)
      setSelection(undefined)
      dispatch(actions.clearSelectedNumber())
    }
  }, [dispatch])

  useEffect(() => {
    if (!data) {
      dispatch(actions.fetchCalls.request(folder))
    }
  }, [dispatch, folder, data])

  const f = filter.toLowerCase()
  const table = useMemo(() => <ReactTable ref={reactTable} className={classNames(compactView && 'compact-view')}
    data={f !== '' && data ? data.filter(c => {
      return c.from.name.toLowerCase().indexOf(f) > -1 ||
        c.from.id.toLowerCase().indexOf(f) > -1 ||
        c.subject.toLowerCase().indexOf(f) > -1
    }) : data
    }
    columns={getColumns(folder, setMenu)} showPagination={false} defaultPageSize={9999999}
    getTrProps={(_: any, row?: TableRow<Call>) => row && ({
      className: (!row.original.read ? 'unread' : '') + ((menu && row.original.id === menu.item.id) || (selected && selected === row.original.id) ? ' selected' : ' '),
      onClick() {
        selectGridItem(row.original, folder, setSelected, setSelection)
      }, onContextMenu(e: React.MouseEvent) {
        setMenu({ target: targetFromEvent(e), item: row.original })
      },
    })}
    sorted={sorted}
    onSortedChange={(newSort, column) => {
      dispatch(actions.requestSetGridSortOrder('recent', [newSort[0]]))
    }}
    minRows={0} NoDataComponent={() => null} />, [compactView, f, data, folder, sorted, menu, selected, selectGridItem, dispatch])

  function onClearAll() {
    showConfirmWindow('IPFX Contact Centre Client', 'Are you sure you want to clear the recent folder \'' + getFolderName(folder) + '\'?', (action: string) => {
      if (action === 'yes') {
        dispatch(actions.clearCalls(folder))
      }
    })
  }

  useEffect(() => {
    return () => {
      if (folder === 'missed') {
        markCallFolderAsRead(folder)
      }
    }
  }, [folder])

  const folders =  useMemo(() => {
    return <TabsHeader className='tab-buttons'>
      {FolderNames.map((f, i) => {
        //const folder = getFolderName(f)
        const title = getFolderName(f) + (f === 'missed' && unread > 0 ? ' (' + unread + ')' : '')
        return <TabButton key={i} active={f === folder} onClick={() => setFolder(f)}>{title}</TabButton>
      })}
    </TabsHeader>
  }, [folder, unread])

  return <>
    {folders}
    <div className='SidebarViewBody'>
      <div className="Header">
        <div className='searchBox'>
          <Input placeholder={getFolderName(folder)} className='search' value={filter} onChange={e => {
            setFilter(e.currentTarget.value)
          }} autocomplete={false} onKeyUp={e => {

          }} />
          <SearchIcon className='searchIcon' />
        </div>
      </div>
      <div className='CallsPage MobileCallsPage'>
        {table}
      </div>
    </div>
    {menu && <ContextMenu state={menu} onClose={() => setMenu(undefined)} onClear={onClearAll} />}
  </ >
}

function useSelectGridItem() {
  const dispatch = useDispatch()

  return useCallback((item: Call, folder: CallFolder, setSelected: (state: string) => void, setSelection: (state: ItemSelected) => void) => {
    if (item.mediaType === 'CALL' || item.mediaType === 'VM') {
      dispatch(actions.setSelectedNumber(item.from.id))
    } else {
      dispatch(actions.clearSelectedNumber())
    }

    setSelected(item.id)

    const id = item.mediaType === 'CALL' || item.mediaType === 'VM' ? item.callKey : item.mediaItemId
    setSelection({ id: id, type: item.mediaType, isOutgoing: (folder === 'placed') })

  }, [dispatch])
}

function CallHistoryIcon({ className, history }: Props) {
  const compact = useCompactView()
  return getIcon(history, classNames('grid-icon', className, compact && 'compact-view'))
}


function callHistortyName(history: Call) {
  if (history.mediaType === 'CALL' || history.mediaType === 'VM') {
    return history.from.name ? history.from.name + (history.from.id !== '' ? ' (' + history.from.id + ')' : '') : history.from.id
  } else if (history.mediaType === 'EMAIL') {
    return history.from.name
  } else if (history.mediaType === 'FB') {
    return history.from.name
  } else if (history.mediaType === 'TC') {
    return history.from.name
  } else if (history.mediaType === 'TWEET') {
    return history.from.name
  } else if (history.mediaType === 'OUT') {
    return history.from.name
  }
}

function getIcon(history: Call, className: string) {
  if (history.mediaType === 'EMAIL') {
    return <div title='Email' className={className}><CallHistoryEmail /></div>
  } else if (history.mediaType === 'FB') {
    return <div title='Facebook' className={className}><FacebookIcon /></div>
  } else if (history.mediaType === 'TC') {
    return <div title='Web Chat' className={className}><WebChatIcon /></div>
  } else if (history.mediaType === 'TWEET') {
    return <div title='Tweet' className={className}><TweetIcon /></div>
  } else if (history.mediaType === 'OUT') {
    return <div title='Out bound' className={className}><OutBoundIcon /></div>
  } else if (history.mediaType === 'VM') {
    return <div title='Callback' className={className}><MediaCallBackIcon /></div>
  }
  else {
    switch (history.deviceType) {
      case DeviceType.Home:
        return <div title='Home' className={className}><CallHistoryHome /></div>
      case DeviceType.Mobile:
        return <div title='Mobile' className={className}><CallHistoryMobile /></div>
      case DeviceType.Other:
        return <div title='Other' className={className}><CallHistoryOther /></div>
      case DeviceType.Softphone:
        return <div title='Softphone' className={className}><CallHistorySoftphone /></div>
      default:
        return <div title='Desk' className={className}><CallHistoryDesk /></div>
    }
  }
}

function ContextMenu({ state: { item: call, target }, onClose, onClear }: { state: MenuState, onClose(): void, onClear(): void }) {
  const dispatch = useDispatch()
  const myId = useSelector(selectMyself);
  const myself = useSelector(s => myId ? selectExtension(s, myId) : undefined)
  const access = useSelector(selectMyAccess)

  if (!myId || !myself || !access) return null

  const enableDial = (call.mediaType === "CALL" || call.mediaType === "VM") && (myself.status === ExtensionStatus.None || myself.status === ExtensionStatus.Released)
  //const enableEmail = call.mediaType !== 'CALL' && call.mediaType !== 'VM'

  return <Menu open={true} onClose={onClose} target={target} placement='bottom-end'>
    {access.features.phoneDial && <MenuItem onClick={() => { if (enableDial) dispatch(actions.ccDial(call.from.id)) }} disabled={!enableDial}>
      <CallHistoryDesk />
      Dial
    </MenuItem>}
    {access.features.phoneDial && <MenuDivider />}
    <MenuItem onClick={() => onClear()}>
      Clear All
    </MenuItem>
  </Menu>
}

function getFolderName(f: CallFolder) {
  return f[0].toUpperCase() + f.substr(1)
}

export function RelativeLongFormatDateTime({ date }: { date: number }) {
  const format = useSelector(selectDateFormating)
  if (!format) return null

  return <>{formatRelativeDateTime(date, format.longDateFormat, format.shortTimeFormat)}</>
}

export function RelativeShortFormatDateTime({ date }: { date: number }) {
  const format = useSelector(selectDateFormating)
  if (!format) return null

  return <>{formatRelativeDateTime(date, format.shortDateFormat, format.shortTimeFormat)}</>
}

export function RelativeShortFormatDate({ date }: { date: number }) {
  const format = useSelector(selectDateFormating)
  if (!format) return null

  return <>{formatRelativeDateTime(date, format.shortDateFormat)}</>
}

/*
type FancyButtonProps<D> = TableProps<D> & {
  color: string
}

export function MyFancyButton<T>({ color, getTrProps, ...props }: FancyButtonProps<T> ) {
  function myGetTrProps() {

  }

  return <ReactTable {...props} getTrProps={} />
}
*/
